import { Formik } from 'formik';
import { useCallback } from 'react';
import { object } from 'yup';

import type { Weld, WeldSymbol } from '@frontend/api';
import { RUNES_CALLOUT_TYPES, WELD_SYMBOLS } from '@frontend/api';
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Select,
} from '@frontend/components';

import { SubFormProps } from '../types';
import { SubFormWrapper } from './shared/SubFormWrapper';
import { createRequiredSelectField } from './shared/validation';

type WeldFormValues = {
  symbol: WeldSymbol;
};

const initialValues: WeldFormValues = {
  symbol: WELD_SYMBOLS.FILLET,
};

const WELD_SYMBOL_OPTIONS = Object.values(WELD_SYMBOLS).map((value) => ({
  value,
  label: value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' '),
}));

const validationSchema = object().shape({
  symbol: createRequiredSelectField('Weld symbol'),
});

export const WeldForm = ({
  onSubmit,
  onCancel,
  renderSelectedType,
}: SubFormProps) => {
  const handleSubmit = useCallback(
    (values: WeldFormValues) => {
      const weld: Weld = {
        calloutType: RUNES_CALLOUT_TYPES.WELD,
        arrowSide: {
          symbol: values.symbol,
          isFieldWeld: false,
          isMeltThru: false,
          isConsumableInsert: false,
          isSpacer: false,
        },
        isAllAround: false,
      };

      const formattedSymbol = values.symbol
        .split('_')
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(' ');
      onSubmit(weld, `${formattedSymbol} weld`);
    },
    [onSubmit],
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnMount
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, errors, touched, isValid, isSubmitting }) => {
        const selectedOption = WELD_SYMBOL_OPTIONS.find(
          (option) => option.value === values.symbol,
        );

        return (
          <SubFormWrapper
            isValid={isValid}
            isSubmitting={isSubmitting}
            onCancel={onCancel}
            renderSelectedType={renderSelectedType}
            submitLabel="Add Weld"
          >
            <FormControl px={1} pb={1}>
              <FormLabel>Weld Symbol</FormLabel>
              <Select
                name="symbol"
                options={WELD_SYMBOL_OPTIONS}
                value={selectedOption}
                onChange={(
                  option: { value: WeldSymbol; label: string } | null,
                ) => {
                  if (option?.value) {
                    setFieldValue('symbol', option.value);
                  }
                }}
                size="sm"
                isClearable={false}
              />
              {errors.symbol && touched.symbol && (
                <FormErrorMessage>{errors.symbol}</FormErrorMessage>
              )}
            </FormControl>
          </SubFormWrapper>
        );
      }}
    </Formik>
  );
};
